import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AlgoliaSearchParams } from "../../../../api/postCall/algoliaSearch";
import CustomTable from "../../../../components/custom-table/custom-table";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { FilterValues } from "../../../../data-model/filterValues";
import { ModalId } from "../../../../enums/modalId";
import { setFilter } from "../../../../redux/slice/filterValuesSlice";
import {
  fetchDataUptoGivenPageNumberFromMedicalPaOrdersAlgolia,
  fetchMedicalPaOrdersAlgoliaData,
  fetchMoreMedicalPaOrdersAlgoliaData,
  resetMedicalPaOrdersAlgoliaData,
  setLoading,
} from "../../../../redux/slice/medicalPa/medicalPaOrdersAlgoliaSlice";
import { resetSingleMedicalPaOrder } from "../../../../redux/slice/medicalPa/singleMedicalPaOrder";
import {
  setMetaData,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import {
  processFilterData,
  transformDateFields,
  transformObjectToKeyValueArray,
} from "../../../../utils/queryConstraint";

import moment from "moment";
import { controlToastState } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import {
  refetchPAOrder,
  RefetchPAOrderPayload,
} from "../../../../api/postCall/refetchPAOrder";
import { createDataForWriteBackTrail } from "../../../../components/modals/imageViewerWithBadgesModal/utils/createDataForWriteBackTrail";
import { MedicalPaOrder } from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { TableNames } from "../../../../enums/tableNames";
import { fetchCountsByMasterAuthStatusByAlgolia } from "../../../../redux/slice/medicalPa/algoliaFacetsCountSlice";
import { fetchMultiFacetsCountFromAlgolia } from "../../../../redux/slice/medicalPa/algoliaMultiFacetsCountSlice";
import { getFilePathsForModal } from "../../external/allPaOrdersForExternal/utils/allPaOrdersForExternalHelper";
import { FILTER_CREATION_CONFIG } from "../../hooks/filterCreationConfig";
import { useFilterOptionsGeneratorFromAlgolia } from "../../hooks/useFilterOptionsGeneratorFromAlgolia";
import { generatePayloadForInitialDataFetch } from "../utils/generatePayloadForInitialDataFetch";
import {
  generateMedicalPaOrdersAlgoliaTableDataForAdmin,
  generateMedicalPaOrdersAlgoliaTableDataForNonAdmin,
  MedicalPaOrdersAlgoliaTableColumnKeys,
  MedicalPaOrdersAlgoliaTableHeadersForAdmin,
  MedicalPaOrdersAlgoliaTableHeadersForNonAdmin,
} from "../utils/medicalPaOrdersTable";

import { apiWorkerManager } from "../../../../workerManagers/workerManager";
const MedicalPaOrdersTable = ({
  medicalPaOrdersTableRef,
  onDataChange,
  onComplete,
  onBulkAssignButtonClick,
  exportingInProgress,
}: {
  medicalPaOrdersTableRef: React.RefObject<HTMLDivElement>;
  onDataChange: (data: any) => void;
  onComplete: boolean;
  onBulkAssignButtonClick: () => void;
  exportingInProgress: boolean;
}) => {
  const { data, loading, showinLineLoader } = useSelector(
    (state: RootState) => state.medicalPaOrdersAlgolia,
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { filters } = useSelector((state: RootState) => state.filterValues);
  const [endIndex, setEndIndex] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [paginationResetCount, setPaginationResetCount] = useState(0);

  const [filterPayload, setFilterPayload] =
    useState<AlgoliaSearchParams | null>();
  const [isTableRefActive, setIsTableRefActive] = useState(false);
  const [initialQueryExecuted, setInitialQueryExecuted] = useState(false);
  const [disableBulkAssignButton, setDisableBulkAssignButton] = useState(true);
  const [tableData, setTableData] = useState<any>([]);
  const [pagesForWhichSelectAllIsEnabled, setPagesForWhichSelectAllIsEnabled] =
    useState<number[]>([]);
  const [isRefetchingPAOrders, setIsRefetchingPAOrders] = useState(false);
  const { filterData, algoliaMultiFacetsCountLoading } =
    useFilterOptionsGeneratorFromAlgolia(FILTER_CREATION_CONFIG);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();

  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const tableAttributes = getTableDataFromContext(
    TableNames.MEDICAL_PA_ORDERS_NEW,
  );

  const orgId = getOrgIdForFetchExternalWorklist();

  const fetchDataWithoutFilterRecursively = () => {
    const payloads = generatePayloadForInitialDataFetch(
      {
        org_id: orgId || "",
        query: "",
        facet_filters: [],
        hits_per_page: 400,
        page: tableAttributes?.lastBatchFetched ?? 0,
      },
      tableAttributes?.lastBatchFetched ?? 0,
      searchTerm ?? "",
    );

    dispatch(fetchDataUptoGivenPageNumberFromMedicalPaOrdersAlgolia(payloads));
  };

  const fetchDataWithSearchTermRecursively = () => {
    const payloads = generatePayloadForInitialDataFetch(
      {
        org_id: orgId || "",
        query: searchTerm,
        facet_filters: [],
        hits_per_page: 400,
        page: tableAttributes?.lastBatchFetched ?? 0,
      },
      tableAttributes?.lastBatchFetched ?? 0,
      searchTerm ?? "",
    );
    dispatch(fetchDataUptoGivenPageNumberFromMedicalPaOrdersAlgolia(payloads));
  };

  const fetchDataWithoutFilter = () => {
    if (initialQueryExecuted) {
      dispatch(
        fetchMedicalPaOrdersAlgoliaData({
          org_id: orgId || "",
          query: searchTerm ?? "",
          facet_filters: [],
          hits_per_page: 400,
          page: 0,
        }),
      );
    } else {
      fetchDataWithoutFilterRecursively();
    }
  };

  const fetchDataWithFilterRecursively = () => {
    const payloads = generatePayloadForInitialDataFetch(
      filterPayload as AlgoliaSearchParams,
      tableAttributes?.lastBatchFetched ?? 0,
      searchTerm ?? "",
    );
    dispatch(fetchDataUptoGivenPageNumberFromMedicalPaOrdersAlgolia(payloads));
  };

  const fetchDataWithFilter = (fromInitialDataFetch: boolean) => {
    if (initialQueryExecuted) {
      dispatch(
        fetchMedicalPaOrdersAlgoliaData(filterPayload as AlgoliaSearchParams),
      );
    } else {
      fetchDataWithFilterRecursively();
    }
  };

  const handleNavigateToNextPage = (rowData: any) => {
    dispatch(resetSingleMedicalPaOrder());
    navigate(`/worklists/evbv/${rowData["id"]}`);
  };

  const handleBulkAssignButtonClick = () => {
    onBulkAssignButtonClick();
  };

  const handleRefetchPAOrderButtonClick = async () => {
    const selectedOrders = tableData
      .filter(
        (row: any) =>
          row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]?.isChecked,
      )
      .map((row: any) => {
        const rowData = JSON.parse(
          row[MedicalPaOrdersAlgoliaTableColumnKeys.ROW_DATA],
        );
        return {
          id: row["id"],
          rowData: rowData,
        };
      });

    if (selectedOrders.length === 0) {
      controlToastState("no-orders-selected");
      return;
    }

    setIsRefetchingPAOrders(true);

    try {
      const promises = selectedOrders.map(async (order) => {
        const rowData = order.rowData;
        const payload: RefetchPAOrderPayload = {
          service_type: rowData?.service_type,
          location: rowData?.location || "",
          order_id: order.id,
          practitioner_name: rowData?.practitioner_name || "",
          regimen_name: rowData?.regimen_name || "",
          date_of_service: rowData?.date_of_service_iso || "",
          org_id: rowData?.facility_id || orgId || "",
          patient_id: rowData?.patient_id || "",
        };
        try {
          await refetchPAOrder(payload);
          return { success: true, orderId: order.id };
        } catch (error) {
          console.error(`Error refetching order ${order.id}:`, error);
          return { success: false, orderId: order.id };
        }
      });

      const results = await Promise.allSettled(promises);
      const successCount = results.filter(
        (r) => r.status === "fulfilled" && r.value.success,
      ).length;
      const failedCount = results.length - successCount;

      if (failedCount === 0) {
        controlToastState("refetch-pa-order-success");
      } else if (successCount === 0) {
        controlToastState("refetch-pa-order-error");
      } else {
        controlToastState("refetch-pa-order-partial-success");
      }
    } finally {
      setIsRefetchingPAOrders(false);
    }
  };

  const exportDataForTable = () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.ANALYTICS_DATE_PICKER_MODAL,
      }),
    );
  };

  const initialActivityBeforeRefreshingData = async () => {
    dispatch(setLoading(true));
    await new Promise((resolve) => setTimeout(resolve, 5000));
    dispatch(setLoading(false));
    dispatch(
      fetchCountsByMasterAuthStatusByAlgolia({
        org_id: orgId || "",
        facet_attribute: "master_auth_status",
        assigned_to: providerDetails?.DocID ?? "",
      }),
    );
    dispatch(
      fetchMultiFacetsCountFromAlgolia({
        org_id: getOrgIdForFetchExternalWorklist(),
      }),
    );
  };

  const handleRefreshButtonClick = async () => {
    await initialActivityBeforeRefreshingData();
    setPagesForWhichSelectAllIsEnabled([]);
    if (isFilterApplied) {
      fetchDataWithFilterRecursively();
    } else if (searchTerm) {
      fetchDataWithSearchTermRecursively();
    } else {
      fetchDataWithoutFilterRecursively();
    }
  };

  const validFilters = useMemo(() => {
    return filters.filter(
      (filter) => filter.name === TableNames.MEDICAL_PA_ORDERS_NEW,
    );
  }, [filters]);

  const tableDataMemo = useMemo(() => {
    if (data && authStatusOptions) {
      const baseData = providerDetails?.IsAdmin
        ? generateMedicalPaOrdersAlgoliaTableDataForAdmin(
            data.hits ?? [],
            authStatusOptions,
          )
        : generateMedicalPaOrdersAlgoliaTableDataForNonAdmin(
            data.hits ?? [],
            authStatusOptions,
          );

      return baseData.map((row: any, index: number) => ({
        ...row,
        ...(providerDetails?.IsAdmin && {
          [MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]: {
            ...row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT],
            isChecked:
              tableData?.[index]?.[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]
                ?.isChecked ?? false,
          },
        }),
      }));
    }
    return [];
  }, [data?.page, authStatusOptions, providerDetails?.IsAdmin]);

  const isFilterApplied = useMemo(() => {
    return (
      validFilters &&
      validFilters.length > 0 &&
      validFilters?.[0]?.["filterCount"] > 0
    );
  }, [validFilters]);

  const handlePropagateEventOnClickInRpaStatusCell = async (id: string) => {
    try {
      dispatch(
        setOpenedModalId({
          id: ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL,
          metaData: {
            showLoading: true,
          },
        }),
      );
      const dataForWriteBackTrail = await createDataForWriteBackTrail(
        id,
        authStatusOptions,
      );
      dispatch(
        setMetaData({
          tabs: dataForWriteBackTrail.tabs,
          showLoading: false,
        }),
      );
    } catch (error) {
      dispatch(
        setMetaData({
          tabs: [],
          showLoading: false,
        }),
      );
    }
  };

  // Initialize and fetch data when component mounts or filter/search changes
  useEffect(() => {
    if (isTableRefActive && filterPayload) {
      if (validFilters && validFilters[0]?.["filterCount"] > 0) {
        fetchDataWithFilter(false);
      } else {
        fetchDataWithoutFilter();
      }
      initialQueryExecuted && setPaginationResetCount((prev) => prev + 1);
      setInitialQueryExecuted(true);
    }
  }, [filterPayload, isTableRefActive]);

  // Set up filter data when data is loaded

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  // Update table data when memo changes
  useEffect(() => {
    setTableData(tableDataMemo);
  }, [tableDataMemo]);

  // Set table ref active when component is mounted
  useEffect(() => {
    if (medicalPaOrdersTableRef.current) {
      setIsTableRefActive(true);
    }
  }, [medicalPaOrdersTableRef]);

  const handleEndIndexOfTable = (endIndex: number) => {
    setEndIndex(endIndex);
  };

  const handlePropagateEventOnClickInRpaStatusTwoCell = async (id: string) => {
    dispatch(
      setOpenedModalId({
        id: ModalId.IMAGE_VIEWER_MODAL,
        metaData: { showLoader: true },
      }),
    );

    try {
      const document = (await FirestoreService.getDocument(
        `/medical_pa_orders/${id}`,
      )) as MedicalPaOrder;
      const filePaths = getFilePathsForModal(document);
      dispatch(
        setMetaData({
          filePaths: filePaths,
          showLoader: false,
        }),
      );
    } catch (error) {
      // Error handled silently
    }
  };

  // Handle pagination and infinite scroll
  useEffect(() => {
    if (
      endIndex === (data?.hits?.length ?? 0) - 1 &&
      (data?.page ?? 0) < (data?.nb_pages ?? 0) - 1
    ) {
      const query = filterPayload ?? "";
      const payload =
        query !== ""
          ? {
              ...filterPayload,
              page: (tableAttributes?.lastBatchFetched ?? 0) + 1,
            }
          : {
              org_id: orgId || "",
              query: searchTerm,
              facet_filters: [],
              hits_per_page: 400,
              page: (tableAttributes?.lastBatchFetched ?? 0) + 1,
            };
      dispatch(
        fetchMoreMedicalPaOrdersAlgoliaData(payload as AlgoliaSearchParams),
      );
    }

    initialQueryExecuted &&
      setTableDataForContext(TableNames.MEDICAL_PA_ORDERS_NEW, {
        lastBatchFetched: data?.page ?? 0,
      });
  }, [endIndex]);

  // Handle filter payload creation
  useEffect(() => {
    const filterData = processFilterData(validFilters);

    const createdFilterQuery = transformObjectToKeyValueArray(filterData).map(
      (item) => item,
    );

    const dateFilterQuery = transformDateFields(filterData);
    const payload = {
      org_id: orgId || "",
      query: searchTerm ?? "",
      facet_filters: createdFilterQuery,
      hits_per_page: 400,
      page: 0,
      ...dateFilterQuery,
    };
    setFilterPayload(payload);
    setPagesForWhichSelectAllIsEnabled([]);
  }, [validFilters, searchTerm]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetMedicalPaOrdersAlgoliaData());
    };
  }, []);

  const handleSelectAll = () => {
    const currentPage = tableAttributes?.currentStartPage;

    if (currentPage == null) return;

    setPagesForWhichSelectAllIsEnabled((prev) =>
      prev.includes(currentPage)
        ? prev.filter((page) => page !== currentPage)
        : [...prev, currentPage],
    );
  };

  // Handle select all functionality
  useEffect(() => {
    if (providerDetails?.IsAdmin) {
      const startIndex = tableAttributes?.startIndex ?? 0;
      const endIndex = tableAttributes?.endIndex ?? 0;

      setTableData(
        tableData.map((row: any, index: number) => {
          // Only modify checkbox status for rows within the startIndex and endIndex range
          if (index >= startIndex && index <= endIndex) {
            return {
              ...row,
              [MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]: {
                ...row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT],
                isChecked: pagesForWhichSelectAllIsEnabled.includes(
                  tableAttributes?.currentStartPage ?? 0,
                )
                  ? true
                  : false,
              },
            };
          }
          // For rows outside the range, preserve all existing data including isChecked status
          return row;
        }),
      );
    }
  }, [pagesForWhichSelectAllIsEnabled, providerDetails?.IsAdmin]);

  // Handle bulk selection data changes
  useEffect(() => {
    if (tableData && tableData.length > 0 && providerDetails?.IsAdmin) {
      const getAllOrderIdsForCheckedAnswers = tableData
        .filter(
          (row: any) =>
            row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]?.isChecked,
        )
        .map((row: any) => row["id"]);
      onDataChange(getAllOrderIdsForCheckedAnswers);
      setDisableBulkAssignButton(getAllOrderIdsForCheckedAnswers.length === 0);
    } else {
      onDataChange([]);
    }
  }, [tableData, providerDetails?.IsAdmin]);

  // Handle completion callback
  useEffect(() => {
    if (onComplete) {
      handleRefreshButtonClick();
    }
  }, [onComplete]);

  return (
    <>
      <div ref={medicalPaOrdersTableRef} className="h-full">
        <CustomTable
          showBulkAssignButton={providerDetails?.IsAdmin ?? false}
          bulkAssignButtonDisabled={disableBulkAssignButton}
          onBulkAssignButtonClick={handleBulkAssignButtonClick}
          showRefetchPAOrderButton={providerDetails?.IsAdmin ?? false}
          refetchPAOrderButtonDisabled={
            disableBulkAssignButton || isRefetchingPAOrders
          }
          isRefetchingPAOrder={isRefetchingPAOrders}
          onRefetchPAOrderButtonClick={handleRefetchPAOrderButtonClick}
          tableHeaders={
            providerDetails?.IsAdmin
              ? MedicalPaOrdersAlgoliaTableHeadersForAdmin
              : MedicalPaOrdersAlgoliaTableHeadersForNonAdmin
          }
          tableData={tableData}
          tableName={TableNames.MEDICAL_PA_ORDERS_NEW}
          itemsPerPage={20}
          pagesPerView={5}
          count={tableData.length ?? 0}
          totalCount={data?.nb_hits ?? 0}
          endIndexOfTable={handleEndIndexOfTable}
          searchingText={(searchText: string) => {
            setSearchTerm(searchText);
          }}
          hideSearchBar={false}
          onRowClick={(rowData: any) => {
            handleNavigateToNextPage(rowData);
            const jsonData = JSON.parse(rowData.rowData);
            const patientId = jsonData?.patient_id;
            const patientMrn = jsonData?.patient_id;
            const patientName =
              jsonData?.first_name + " " + jsonData?.last_name;
            const dateOfService = jsonData?.date_of_service;
            const dateOfWork = moment(jsonData?.created_at).format(
              "YYYY-MM-DD",
            );
            const drugName = jsonData?.regimen_name;
            const providerName = jsonData?.practitioner_name;

            apiWorkerManager.enqueueAndProcess({
              patient_id: patientId,
              org_id: orgId,
              order_id: rowData.id,
              event_name: EventsName.ORDER_CLICKED,
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.WORKLIST,
                payer_name: jsonData?.coverage?.primary?.payer_name ?? "",
                patient_name: patientName,
                drug_name: drugName ?? "",
                provider_name: providerName ?? "",
                date_of_work: dateOfWork,
                date_of_service: dateOfService ?? "",
                patient_mrn: patientMrn ?? "",
                source: ScreenNamesForBigQuery.ALL_PA_ORDERS,
              },
            });
          }}
          isFetching={loading}
          showExportButton={true}
          onExportButtonClick={exportDataForTable}
          showInLineLoader={showinLineLoader}
          showRefreshButton={true}
          onRefreshButton={handleRefreshButtonClick}
          isDisabled={loading}
          onSelectAll={providerDetails?.IsAdmin ? handleSelectAll : undefined}
          toggleCheckboxSelection={
            providerDetails?.IsAdmin
              ? (id: string) => {
                  setTableData(
                    tableData.map((row: any) => ({
                      ...row,
                      [MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]: {
                        ...row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT],
                        isChecked:
                          id ===
                          row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT].id
                            ? !row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]
                                .isChecked
                            : row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]
                                .isChecked,
                      },
                    })),
                  );
                }
              : undefined
          }
          onComplete={handleRefreshButtonClick}
          showFilterButton={!loading && !algoliaMultiFacetsCountLoading}
          filterSectionsData={filterData}
          onFilterApplyClicked={handleFilterApply}
          requestForPaginationReset={paginationResetCount}
          exportingInProgress={exportingInProgress}
          onPropagateEventOnClickInRpaStatusCell={(id: string) => {
            handlePropagateEventOnClickInRpaStatusCell(id);
          }}
          onPropagateEventOnClickInRpaStatusTwoCell={(id: string) => {
            handlePropagateEventOnClickInRpaStatusTwoCell(id);
          }}
          isCheckboxSelected={pagesForWhichSelectAllIsEnabled.includes(
            tableAttributes?.currentStartPage ?? 0,
          )}
          screenName={ScreenNamesForBigQuery.WORKLIST}
          shouldLogEvent={true}
        />
      </div>
    </>
  );
};

export default MedicalPaOrdersTable;
